/**
 * WeatherBand — teaser band for the front page, laid over the hero as a
 * CNN-style lower third.
 *
 * The slogans live in a JSON file editors maintain in the site, so the payload
 * is treated as untrusted: every entry is validated and every href passes an
 * allowlist before it reaches the DOM.
 *
 * Rendering is hydration safe. The server and the first client render agree on
 * nothing at all; the network, the hero lookup and the random pick only happen
 * inside mount effects, and the band appears once the payload is ready.
 *
 * Placement: the component owns a host element it appends to the hero slider
 * and portals itself into. It never writes to the hero, which is already a
 * positioned containing block; all geometry lives in the stylesheet. When no
 * hero can be found the band renders in flow in its slot instead.
 */

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { defineMessages, useIntl } from 'react-intl';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';

const SLOGANS_URL = '/++api++/banner_slogans.json/@@download/file';
const DEFAULT_LINE = 'We have done this before.';
const DEFAULT_HREF = '/since-2001';
const HERO_SELECTOR = '#page-document .blocks-group-wrapper > .block.slider';
const HERO_POLL_MS = 25;
const HERO_WINDOW_MS = 200;

// Shared across mounts so a client-side route change does not refetch. The
// cache holds the validated entries, never the pick, so every mount rerolls.
let entriesCache = null;

export function __resetCacheForTests() {
  entriesCache = null;
}

/**
 * Editor-supplied hrefs are limited to site-relative paths and http(s) URLs.
 * Anything else — javascript:, data:, protocol-relative — falls back to the
 * default target rather than reaching the anchor. Relative candidates are
 * resolved against a sentinel origin, because WHATWG URL parsing treats
 * `/\host`, `/ <tab> /host` and similar as protocol-relative; the candidate
 * only passes when the resolved host stays on the sentinel.
 */
export function sanitizeHref(href) {
  if (typeof href !== 'string') return DEFAULT_HREF;
  if (/^https?:\/\//i.test(href)) return href;
  if (!href.startsWith('/')) return DEFAULT_HREF;
  try {
    if (
      new URL(href, 'https://weatherband.invalid').host ===
      'weatherband.invalid'
    ) {
      return href;
    }
  } catch (error) {
    // Unparseable — fall through to the default target.
  }
  return DEFAULT_HREF;
}

function usableText(value) {
  return typeof value === 'string' && value.trim() !== '';
}

const messages = defineMessages({
  region: {
    id: 'Conference teaser',
    defaultMessage: 'Conference teaser',
  },
});

const WeatherBand = () => {
  const intl = useIntl();
  const [state, setState] = useState({ status: 'loading', entry: null });
  const [host, setHost] = useState({ status: 'hunting', node: null });

  useEffect(() => {
    let cancelled = false;
    let interval = null;
    let node = null;

    const stopPolling = () => {
      if (interval !== null) {
        clearInterval(interval);
        interval = null;
      }
    };

    const attach = (hero) => {
      stopPolling();
      node = document.createElement('div');
      node.className = 'weather-band-overlay-host';
      node.setAttribute('data-weather-band-overlay', '');
      hero.appendChild(node);
      setHost({ status: 'attached', node });
    };

    const hero = document.querySelector(HERO_SELECTOR);
    if (hero) {
      attach(hero);
    } else {
      // The hero ships in the server-rendered markup, so it is normally found
      // on this first look. The short poll only covers a render hiccup; once
      // the window closes the band gives up and falls back to its slot.
      const deadline = Date.now() + HERO_WINDOW_MS;
      interval = setInterval(() => {
        if (cancelled) {
          stopPolling();
          return;
        }
        const late = document.querySelector(HERO_SELECTOR);
        if (late) {
          attach(late);
          return;
        }
        if (Date.now() >= deadline) {
          stopPolling();
          setHost({ status: 'absent', node: null });
        }
      }, HERO_POLL_MS);
    }

    return () => {
      cancelled = true;
      stopPolling();
      if (node) node.remove();
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    const pick = (entries) => {
      if (cancelled) return;
      setState({
        status: 'ready',
        entry: entries[Math.floor(Math.random() * entries.length)],
      });
    };

    // Single warn call site, so one fetch attempt can never warn twice.
    const fail = (reason) => {
      if (cancelled) return;
      // eslint-disable-next-line no-console
      console.warn(`WeatherBand: ${reason}, the band stays hidden.`);
      setState({ status: 'failed', entry: null });
    };

    const load = async () => {
      if (entriesCache) {
        pick(entriesCache);
        return;
      }

      let entries;
      try {
        const response = await fetch(SLOGANS_URL, {
          cache: 'no-store',
          headers: { Accept: 'application/json' },
          signal: controller.signal,
        });
        if (!response.ok) {
          fail(`the slogans file answered with status ${response.status}`);
          return;
        }
        const payload = await response.json();
        if (!Array.isArray(payload)) {
          fail('the slogans file is not a JSON array');
          return;
        }
        if (payload.length === 0) {
          fail('the slogans file is empty');
          return;
        }
        entries = payload.filter((entry) => usableText(entry?.beat));
        if (entries.length === 0) {
          fail('no slogan carries a usable beat');
          return;
        }
      } catch (error) {
        if (controller.signal.aborted || error?.name === 'AbortError') return;
        fail(
          `the slogans file could not be read (${error?.message ?? 'unknown error'})`,
        );
        return;
      }

      entriesCache = entries;
      pick(entries);
    };

    load();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  if (state.status !== 'ready' || host.status === 'hunting') {
    return null;
  }

  const entry = state.entry;
  const line = usableText(entry.line) ? entry.line : DEFAULT_LINE;
  const href = sanitizeHref(entry.href);
  const placement = host.node
    ? 'weather-band--overlay'
    : 'weather-band--inflow';

  const band = (
    // eslint-disable-next-line jsx-a11y/no-redundant-roles -- the brief asks for an explicit role="region" landmark
    <section
      role="region"
      aria-label={intl.formatMessage(messages.region)}
      className={`weather-band weather-band--ready ${placement}`}
    >
      <div className="weather-band__content">
        <span className="weather-band__beat">{entry.beat}</span>
        <UniversalLink
          className="weather-band__line"
          href={flattenToAppURL(href)}
        >
          {line}
        </UniversalLink>
      </div>
    </section>
  );

  return host.node ? createPortal(band, host.node) : band;
};

export default WeatherBand;
